import { FC, ReactNode, useContext, useMemo, useState } from 'react';
import { Field } from 'redux-form';
import Markdown from 'shared/components/Markdown';
import styled, { DefaultTheme, ThemeContext } from 'styled-components';

import LocationSearchInput from 'inquiry/components/InputAddress/LocationSearchInput';
import TextBase from 'inquiry/components/TextBase';
import ThemedFormInput from 'inquiry/components/ThemedFormInput';
import { parseEmptyStringToNull } from 'inquiry/lib/form';
import { containerSSTStyles, labelSSTStyles } from 'inquiry/mixins/form';
import { CountryCode, POSTAL_CODE_PRIMARY_COUNTRIES, US_STATES } from 'lib/constants/locales';
import { InputStyles } from 'lib/interfaces/input-styles';
import { UsStateInputMethod } from 'lib/interfaces/ThemeSerialized';
import {
  AddressAutocompleteMethod,
  InputAddressComponent,
  PlaceType,
} from 'lib/interfaces/ui-components';
import { Rule, withMessage } from 'lib/utils/formValidation';
import ThemedFormSelect from 'verify/components/FormControls/ThemedFormSelect';

interface AddressValidators {
  autocompleteClosed: Rule[];
  addressStreet1: Rule[];
  addressStreet2: Rule[];
  addressCity: Rule[];
  addressSubdivision: Rule[];
  addressPostalCode: Rule[];
  addressBusinessName: Rule[];
}

interface ThemedFormAddressInputProps {
  // field keys
  fieldKeyAddressStreet1: string;
  fieldKeyAddressStreet2: string;
  fieldKeyAddressCity: string;
  fieldKeyAddressSubdivision: string;
  fieldKeyAddressPostalCode: string;
  fieldKeyAddressBusinessName?: string;
  // translations
  editAddressManuallyPrompt?: string;
  label: string;
  placeholderAddressStreet1?: string;
  placeholderAddressStreet2?: string;
  placeholderAddressCity?: string;
  placeholderAddressSubdivision?: string;
  placeholderAddressSubdivisionUs?: string;
  placeholderAddressPostalCode?: string;
  placeholderAddressPostalCodeUs?: string;
  placeholderAddressBusinessName?: string;
  placeholderAutocomplete?: string;
  placeholderAutocompletePostalCode?: string;
  validateAddressStreet1: Rule[];
  validateAddressStreet2: Rule[];
  validateAddressCity: Rule[];
  validateAddressSubdivision: Rule[];
  validateAddressPostalCode: Rule[];
  validateAddressBusinessName: Rule[];
  serverErrorAddressStreet1?: string;
  serverErrorAddressStreet2?: string;
  serverErrorAddressCity?: string;
  serverErrorAddressSubdivision?: string;
  serverErrorAddressPostalCode?: string;
  serverErrorAddressBusinessName?: string;
  // Other
  setField: (field: string, value: string) => void;
  selectedCountryCode: CountryCode;
  forceOpen: boolean;
  addressAutocompleteMethod: AddressAutocompleteMethod;
  addressSubdivisionFormat?: 'short_name' | 'long_name';
  placeType?: PlaceType;
  disabled?: boolean;
  optional?: boolean;
  showBusinessNameField: boolean;
  styles: InputAddressComponent['styles'];
}

const Container = styled('div')<{ styles?: InputAddressComponent['styles'] }>`
  ${({ styles }) => containerSSTStyles(styles)}
  ${({ styles }) => labelSSTStyles(styles)}
`;

const ExpandedContainer = styled('div')<{ styles?: InputAddressComponent['styles'] }>`
  ${({ styles }) => containerSSTStyles(styles)}
  ${({ styles }) => labelSSTStyles(styles)}

  // override default margin-bottom because the last input field already adds it
  margin-bottom: 0;
`;

const ThemedFormAddressInput: FC<ThemedFormAddressInputProps> = ({
  // field keys
  fieldKeyAddressStreet1,
  fieldKeyAddressStreet2,
  fieldKeyAddressCity,
  fieldKeyAddressSubdivision,
  fieldKeyAddressPostalCode,
  fieldKeyAddressBusinessName,
  // translations
  editAddressManuallyPrompt,
  label,
  placeholderAddressStreet1,
  placeholderAddressStreet2,
  placeholderAddressCity,
  placeholderAddressSubdivision,
  placeholderAddressSubdivisionUs,
  placeholderAddressPostalCode,
  placeholderAddressPostalCodeUs,
  placeholderAddressBusinessName,
  placeholderAutocomplete,
  placeholderAutocompletePostalCode,
  // validation rules
  validateAddressStreet1,
  validateAddressStreet2,
  validateAddressCity,
  validateAddressSubdivision,
  validateAddressPostalCode,
  validateAddressBusinessName,
  // server errors
  serverErrorAddressStreet1,
  serverErrorAddressStreet2,
  serverErrorAddressCity,
  serverErrorAddressSubdivision,
  serverErrorAddressPostalCode,
  serverErrorAddressBusinessName,

  // Other
  setField,
  selectedCountryCode,
  forceOpen,
  addressAutocompleteMethod,
  addressSubdivisionFormat,
  placeType,
  disabled,
  optional,
  showBusinessNameField,
  styles,
}: ThemedFormAddressInputProps) => {
  // google maps silently fails to load on older unsupported versions of Safari
  // set input address to be autoexpanded on initial render if places is not defined
  // it should still be safe to render LocationSearchInput.
  // We tested setting window.google.maps.places to undefined and autocomplete just silently fails
  // We also tested loading google maps api after input address rendered, and autocomplete
  // does work after google maps api is loaded succesfully, so we don't really have a race condition
  // if input address is on the first screen
  // (in practice, google maps api almost always loads before the inquiry anyways)
  const [manualInputOpen, setManualInputOpen] = useState(!window.google?.maps?.places);
  const theme = useContext(ThemeContext);

  const validate = useMemo<AddressValidators>(() => {
    return {
      autocompleteClosed: optional
        ? []
        : [
            // This is used to flag validation errors when the autocomplete isn't
            // expanded. The fields with the actual validation functions don't
            // exist when not expanded so their validators won't run.
            // This won't actually be shown to the user.
            withMessage('Fill out the details of your current address', () => false),
          ],

      addressStreet1: validateAddressStreet1,
      addressStreet2: validateAddressStreet2,
      addressCity: validateAddressCity,
      addressSubdivision: validateAddressSubdivision,
      addressPostalCode: validateAddressPostalCode,
      addressBusinessName: validateAddressBusinessName,
    };
  }, [
    validateAddressStreet1,
    validateAddressStreet2,
    validateAddressCity,
    validateAddressSubdivision,
    validateAddressPostalCode,
    validateAddressBusinessName,
    optional,
  ]);

  const isPostalCodePrimaryCountry = POSTAL_CODE_PRIMARY_COUNTRIES.includes(selectedCountryCode);

  const autocompleteInputName = isPostalCodePrimaryCountry
    ? fieldKeyAddressPostalCode
    : fieldKeyAddressStreet1;
  const autocompleteInputPlaceholder = isPostalCodePrimaryCountry
    ? placeholderAutocompletePostalCode
    : placeholderAutocomplete;
  const postalCodeInputPlaceholder =
    selectedCountryCode === 'US' ? placeholderAddressPostalCodeUs : placeholderAddressPostalCode;

  const renderAddressFields = () => {
    if (
      manualInputOpen ||
      forceOpen ||
      addressAutocompleteMethod === AddressAutocompleteMethod.None
    ) {
      return renderCountryOrderedAddress();
    } else {
      const placeholder =
        theme.inputStyle === InputStyles.Outlined ? autocompleteInputPlaceholder : label;

      return (
        <Container styles={styles}>
          <Field
            data-test='address-street-1'
            selectedCountryCode={selectedCountryCode}
            fieldKeyAddressStreet1={fieldKeyAddressStreet1}
            fieldKeyAddressStreet2={fieldKeyAddressStreet2}
            fieldKeyAddressCity={fieldKeyAddressCity}
            fieldKeyAddressSubdivision={fieldKeyAddressSubdivision}
            fieldKeyAddressPostalCode={fieldKeyAddressPostalCode}
            fieldKeyAddressBusinessName={fieldKeyAddressBusinessName}
            showBusinessNameField={showBusinessNameField}
            addressSubdivisionFormat={addressSubdivisionFormat}
            name={autocompleteInputName}
            id={autocompleteInputName}
            setField={handleAddressChange}
            component={LocationSearchInput}
            disabled={disabled}
            type='text'
            parse={parseEmptyStringToNull}
            validate={validate.autocompleteClosed}
            placeholder={placeholder}
            placeType={placeType}
            styles={styles}
          />
          {!disabled && editAddressManuallyPrompt && (
            // Hide the edit address button if prompt is blank
            <TextBase styles={styles?.['text-style']['expand-button'].base}>
              <a
                id='address_input_section__manual_edit'
                data-test='address_input_section__manual_edit'
                onClick={handleOpenManualInput}
                tabIndex={0}
              >
                {editAddressManuallyPrompt}
              </a>
            </TextBase>
          )}
        </Container>
      );
    }
  };

  const getFieldOrder = (
    selectedCountryCode: CountryCode,
    street1Field: ReactNode,
    street2Field: ReactNode,
    cityField: ReactNode,
    subdivisionField: ReactNode,
    postalCodeField: ReactNode,
    businessNameField: ReactNode,
  ) => {
    // Address formats are from http://bitboost.com/ref/international-address-formats.html

    switch (selectedCountryCode) {
      case 'AR':
      case 'GL':
      case 'IS':
        return [[businessNameField], [street1Field], [street2Field], [postalCodeField, cityField]];

      case 'AT':
      case 'BE':
      case 'DK':
        return [[businessNameField], [street1Field, street2Field], [postalCodeField, cityField]];

      case 'BR':
      case 'CR':
        return [
          [businessNameField],
          [street1Field, street2Field],
          [cityField, subdivisionField],
          [postalCodeField],
        ];

      case 'CA':
        return [
          [businessNameField],
          [street2Field, street1Field],
          [cityField, subdivisionField, postalCodeField],
        ];

      case 'CN':
        return [
          [businessNameField],
          [street2Field],
          [street1Field, cityField],
          [subdivisionField, postalCodeField],
        ];

      case 'IT':
        return [
          [businessNameField],
          [street1Field, street2Field],
          [postalCodeField, cityField, subdivisionField],
        ];

      // Note that while GB is postal code primary, we still render it as the
      // last field after autocompletion. This is what Amazon UK does.
      case 'AU':
      case 'GB':
      case 'PH':
      case 'US':
        return [
          [businessNameField],
          [street1Field, street2Field],
          [cityField, subdivisionField, postalCodeField],
        ];

      case 'FI':
      case 'FR':
      case 'DE':
      case 'IL':
        return [[businessNameField], [street1Field, street2Field], [postalCodeField, cityField]];

      case 'JP':
      case 'KR':
        return [
          [businessNameField],
          [street2Field, street1Field],
          [cityField, subdivisionField, postalCodeField],
        ];

      case 'IN':
        return [[businessNameField], [street2Field], [street1Field], [cityField, postalCodeField]];

      case 'FJ':
        return [[businessNameField], [street1Field, street2Field], [cityField]];

      case 'HK':
        return [[businessNameField], [street2Field], [street1Field], [cityField]];

      case 'EE':
      case 'CZ':
      case 'TW':
      case 'ID':
      default:
        return [[businessNameField], [street1Field, street2Field], [cityField, postalCodeField]];
    }
  };

  const handleAddressChange = (field: string, value: string) => {
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    setField(field, parseEmptyStringToNull(value));
    setManualInputOpen(true);
  };

  /**
   * Returns the postal code input as an autocomplete search input for postal code primary countries.
   */
  const getPostalCodeField = () => {
    if (autocompleteInputName === fieldKeyAddressPostalCode) {
      return (
        <Field
          data-test='address-postal-code'
          autoComplete='postal-code'
          selectedCountryCode={selectedCountryCode}
          fieldKeyAddressStreet1={fieldKeyAddressStreet1}
          fieldKeyAddressStreet2={fieldKeyAddressStreet2}
          fieldKeyAddressCity={fieldKeyAddressCity}
          fieldKeyAddressSubdivision={fieldKeyAddressSubdivision}
          fieldKeyAddressPostalCode={fieldKeyAddressPostalCode}
          fieldKeyAddressBusinessName={fieldKeyAddressBusinessName}
          showBusinessNameField={showBusinessNameField}
          addressSubdivisionFormat={addressSubdivisionFormat}
          name={fieldKeyAddressPostalCode}
          id={fieldKeyAddressPostalCode}
          disabled={disabled}
          placeholder={postalCodeInputPlaceholder}
          type='text'
          parse={parseEmptyStringToNull}
          validate={validate.addressPostalCode}
          setField={setField}
          component={LocationSearchInput}
          placeType={placeType}
          serverError={serverErrorAddressPostalCode}
          styles={styles}
        />
      );
    } else {
      return (
        <Field
          data-test='address-postal-code'
          autoComplete='postal-code'
          name={fieldKeyAddressPostalCode}
          id={fieldKeyAddressPostalCode}
          disabled={disabled}
          placeholder={postalCodeInputPlaceholder}
          type='text'
          parse={parseEmptyStringToNull}
          validate={validate.addressPostalCode}
          setField={setField}
          component={ThemedFormInput}
          serverError={serverErrorAddressPostalCode}
          styles={styles}
        />
      );
    }
  };

  const renderCountryOrderedAddress = () => {
    const street2Field = (
      <Field
        data-test='address-street-2'
        autoComplete='address-line2'
        name={fieldKeyAddressStreet2}
        id={fieldKeyAddressStreet2}
        component={ThemedFormInput}
        disabled={disabled}
        type='text'
        placeholder={placeholderAddressStreet2}
        parse={parseEmptyStringToNull}
        validate={validate.addressStreet2}
        serverError={serverErrorAddressStreet2}
        styles={styles}
      />
    );

    const cityField = (
      <Field
        data-test='address-city'
        autoComplete='address-level2'
        name={fieldKeyAddressCity}
        id={fieldKeyAddressCity}
        component={ThemedFormInput}
        disabled={disabled}
        type='text'
        placeholder={placeholderAddressCity}
        parse={parseEmptyStringToNull}
        validate={validate.addressCity}
        serverError={serverErrorAddressCity}
        styles={styles}
      />
    );
    const subdivisionField = (
      <SubdivisionFormField
        selectedCountryCode={selectedCountryCode}
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        disabled={disabled}
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        placeholderAddressSubdivisionUs={placeholderAddressSubdivisionUs}
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        placeholderAddressSubdivision={placeholderAddressSubdivision}
        theme={theme}
        fieldKeyAddressSubdivision={fieldKeyAddressSubdivision}
        validate={validate.addressSubdivision}
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        serverError={serverErrorAddressSubdivision}
        styles={styles}
      />
    );

    const rows = getFieldOrder(
      selectedCountryCode,
      getStreet1Field(),
      street2Field,
      cityField,
      subdivisionField,
      getPostalCodeField(),
      getBusinessNameField(),
    );

    return rows.map((row, rowIndex) => (
      <div key={rowIndex} className='row'>
        {row.filter(Boolean).map((field, index) => (
          <div key={index} className='col-sm-12'>
            {field}
          </div>
        ))}
      </div>
    ));
  };

  const handleOpenManualInput = () => {
    setManualInputOpen(true);
  };

  const getStreet1Field = () => {
    if (
      autocompleteInputName === fieldKeyAddressStreet1 &&
      addressAutocompleteMethod === AddressAutocompleteMethod.GoogleMaps
    ) {
      return (
        <Field
          data-test='address-street-1'
          autoComplete='address-line1'
          selectedCountryCode={selectedCountryCode}
          fieldKeyAddressStreet1={fieldKeyAddressStreet1}
          fieldKeyAddressStreet2={fieldKeyAddressStreet2}
          fieldKeyAddressCity={fieldKeyAddressCity}
          fieldKeyAddressSubdivision={fieldKeyAddressSubdivision}
          fieldKeyAddressPostalCode={fieldKeyAddressPostalCode}
          fieldKeyAddressBusinessName={fieldKeyAddressBusinessName}
          showBusinessNameField={showBusinessNameField}
          addressSubdivisionFormat={addressSubdivisionFormat}
          name={fieldKeyAddressStreet1}
          id={fieldKeyAddressStreet1}
          disabled={disabled}
          placeholder={placeholderAddressStreet1}
          type='text'
          parse={parseEmptyStringToNull}
          validate={validate.addressStreet1}
          setField={setField}
          component={LocationSearchInput}
          placeType={placeType}
          serverError={serverErrorAddressStreet1}
          styles={styles}
        />
      );
    } else {
      return (
        <Field
          data-test='address-street-1'
          autoComplete='address-line1'
          name={fieldKeyAddressStreet1}
          id={fieldKeyAddressStreet1}
          disabled={disabled}
          placeholder={placeholderAddressStreet1}
          type='text'
          parse={parseEmptyStringToNull}
          validate={validate.addressStreet1}
          component={ThemedFormInput}
          serverError={serverErrorAddressStreet1}
          styles={styles}
        />
      );
    }
  };

  const getBusinessNameField = () => {
    if (!showBusinessNameField) {
      return null;
    }
    if (addressAutocompleteMethod === AddressAutocompleteMethod.GoogleMaps) {
      return (
        <Field
          data-test='address-business-name'
          autoComplete='address-line1'
          selectedCountryCode={selectedCountryCode}
          fieldKeyAddressStreet1={fieldKeyAddressStreet1}
          fieldKeyAddressStreet2={fieldKeyAddressStreet2}
          fieldKeyAddressCity={fieldKeyAddressCity}
          fieldKeyAddressSubdivision={fieldKeyAddressSubdivision}
          fieldKeyAddressPostalCode={fieldKeyAddressPostalCode}
          fieldKeyAddressBusinessName={fieldKeyAddressBusinessName}
          showBusinessNameField={showBusinessNameField}
          addressSubdivisionFormat={addressSubdivisionFormat}
          name={fieldKeyAddressBusinessName}
          id={fieldKeyAddressBusinessName}
          disabled={disabled}
          placeholder={placeholderAddressBusinessName}
          type='text'
          setField={setField}
          component={LocationSearchInput}
          placeType={placeType}
          parse={parseEmptyStringToNull}
          validate={validate.addressBusinessName}
          serverError={serverErrorAddressBusinessName}
          styles={styles}
        />
      );
    } else {
      return (
        <Field
          data-test='address-business-name'
          name={fieldKeyAddressBusinessName}
          id={fieldKeyAddressBusinessName}
          component={ThemedFormInput}
          disabled={disabled}
          type='text'
          placeholder={placeholderAddressBusinessName}
          parse={parseEmptyStringToNull}
          validate={validate.addressBusinessName}
          serverError={serverErrorAddressBusinessName}
          styles={styles}
        />
      );
    }
  };

  return (
    <ExpandedContainer styles={styles}>
      {theme.inputStyle === InputStyles.Outlined && label && (
        <Markdown<'label'> wrapper='label' wrapperProps={{ htmlFor: fieldKeyAddressStreet1 }}>
          {label}
        </Markdown>
      )}
      {renderAddressFields()}
    </ExpandedContainer>
  );
};

export default ThemedFormAddressInput;

function SubdivisionFormField({
  selectedCountryCode,
  disabled,
  theme,
  placeholderAddressSubdivisionUs,
  placeholderAddressSubdivision,
  fieldKeyAddressSubdivision,
  validate,
  serverError,
  styles,
}: {
  selectedCountryCode: CountryCode;
  disabled: boolean;
  theme: DefaultTheme;
  placeholderAddressSubdivisionUs: string;
  placeholderAddressSubdivision: string;
  fieldKeyAddressSubdivision: string;
  validate: Rule[];
  serverError: string;
  styles: InputAddressComponent['styles'];
}) {
  if (selectedCountryCode === 'US' && theme.usStateInputMethod === UsStateInputMethod.Select) {
    return (
      // TODO(tianen): ignoring SST on this component for now, since currently
      // input address styles only supports input text styles
      <Field
        data-test='address-subdivision'
        autoComplete='address-level1'
        name={fieldKeyAddressSubdivision}
        id={fieldKeyAddressSubdivision}
        component={ThemedFormSelect}
        disabled={disabled}
        type='text'
        placeholder={placeholderAddressSubdivisionUs}
        parse={parseEmptyStringToNull}
        validate={validate}
        serverError={serverError}
      >
        <option value='' selected disabled>
          {placeholderAddressSubdivisionUs}
        </option>
        {/* @ts-expect-error [strict-migration] The following line is not strict; please fix! */}
        {Object.keys(US_STATES).map((code: keyof typeof US_STATES) => (
          // NOTE: this isn't translated, but refers to US states only
          <option key={code} value={US_STATES[code]}>
            {code}
          </option>
        ))}
      </Field>
    );
  } else {
    return (
      <Field
        data-test='address-subdivision'
        autoComplete='address-level1'
        name={fieldKeyAddressSubdivision}
        id={fieldKeyAddressSubdivision}
        component={ThemedFormInput}
        disabled={disabled}
        type='text'
        placeholder={
          selectedCountryCode === 'US'
            ? placeholderAddressSubdivisionUs
            : placeholderAddressSubdivision
        }
        parse={parseEmptyStringToNull}
        validate={validate}
        serverError={serverError}
        styles={styles}
      />
    );
  }
}
